import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

// Filet de sécurité de dernier recours : capture toute erreur de rendu React non
// interceptée (page à la demande impossible à charger, exception d'affichage, etc.)
// pour éviter l'écran blanc et proposer à l'utilisatrice de recharger l'application.
// Les données ne sont pas touchées (l'erreur est purement côté affichage).
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Point de capture unique et centralisé. Trace technique dans la console du
    // navigateur, sans exposer le détail à l'utilisatrice. C'est ici qu'on branchera
    // plus tard un suivi d'erreurs en production (cf. audit du 09/07/2026, piste Aj3).
    console.error('Erreur de rendu interceptée :', error, info.componentStack)
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-bdspBg flex items-center justify-center p-6">
          <div className="max-w-md space-y-4 text-center">
            <h1 className="text-xl font-semibold text-bdspInk">Une erreur est survenue</h1>
            <p className="text-bdspSlate">
              L'application a rencontré un problème inattendu. Tes données sont en sécurité.
              Recharge la page pour continuer.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-lg bg-bdspIndigo px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
            >
              Recharger la page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
